import _styled from "styled-components";
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {cmpClassName, execFn, getRandomString} from "../../../common/generic-react.functions.js";
import Label from "../label/label.component.js";
import {useValidationCheckbox} from "./checkbox.hooks.js";
import {CheckboxContainer, CheckboxLabelContainer} from "./checkbox.styles.js";
function MainCheckbox({
  children,
  bold,
  checked,
  color = "var(--secondaryColor)",
  textColor = "#2A394E",
  disabled,
  label,
  name,
  position = "left",
  readOnly,
  required,
  size = "base",
  rules,
  values = [true, false],
  onChange
}, ref) {
  const [check, _setCheck] = useState(!!checked);
  const inputRef = useRef(null);
  const {
    errorMessage: [errorMessage],
    validateOnChange: [validateOnChange, setValidateOnChange],
    isValidCheckbox
  } = useValidationCheckbox({
    inputRef,
    rules
  });
  useEffect(() => {
    _setCheck(!!checked);
  }, [checked]);
  const setCheck = useCallback((isChecked) => {
    if (!disabled && !readOnly) {
      _setCheck(isChecked);
      execFn(onChange, isChecked);
    }
  }, [disabled, onChange, readOnly]);
  useImperativeHandle(ref, () => {
    return {
      el: inputRef.current,
      check: () => setCheck(true),
      uncheck: () => setCheck(false),
      toggle: () => setCheck(!check),
      isChecked: !!inputRef.current?.checked,
      valid: (e) => {
        const valid = isValidCheckbox(e);
        setValidateOnChange(!valid);
        return valid;
      },
      setValidateOnChange: (v = false) => setValidateOnChange(v),
      value: check ? values[0] : values[1]
    };
  }, [check, isValidCheckbox, setCheck, setValidateOnChange, values]);
  const _onChange = useCallback((v) => {
    setCheck(v);
    if (validateOnChange) {
      isValidCheckbox(true);
    }
  }, [isValidCheckbox, setCheck, validateOnChange]);
  const checkIcon = useMemo(() => /* @__PURE__ */ React.createElement("div", {
    className: "checkbox"
  }), []);
  const inputId = useMemo(() => name || getRandomString(), [name]);
  const text = useMemo(() => label || children, [children, label]);
  return /* @__PURE__ */ React.createElement(CheckboxContainer, {
    className: cmpClassName("checkbox"),
    size
  }, /* @__PURE__ */ React.createElement(CheckboxLabelContainer, {
    tag: "label",
    color,
    disabled: !!disabled,
    position,
    readOnly: !!readOnly,
    size,
    htmlFor: inputId,
    errorMessage
  }, /* @__PURE__ */ React.createElement(_StyledInput, {
    type: "checkbox",
    ref: inputRef,
    id: inputId,
    name,
    disabled: disabled || readOnly,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    required,
    checked: check,
    onChange: (e) => _onChange(e.target.checked)
  }), position === "left" && checkIcon, text && /* @__PURE__ */ React.createElement(Label, {
    tag: "div",
    bold,
    size,
    required: required && position,
    color: textColor
  }, text), position === "right" && checkIcon), errorMessage && /* @__PURE__ */ React.createElement("div", {
    className: "checkbox__error"
  }, errorMessage));
}
const Checkbox = /* @__PURE__ */ React.memo(/* @__PURE__ */ forwardRef(MainCheckbox));
Checkbox.displayName = "Checkbox";
export default Checkbox;
var _StyledInput = _styled("input").withConfig({
  displayName: "checkboxcomponent___StyledInput",
  componentId: "sc-k7qrxf-0"
})({
  position: "absolute",
  display: "none"
});
